import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut, Camera, CheckCircle2, Clock, Image as ImageIcon, 
  X, SwitchCamera, Bell, ArrowRight, AlertTriangle, 
  ChevronRight, BookOpen, MessageSquare, Loader2
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import Webcam from 'react-webcam';

// --- IMPORT DE MATHLIVE ---
import 'mathlive';

import Loading from './Loading';
import SmartEx from './smartex';
import { API_URLS } from '../config/api';
import { subscribeUserToPush } from '../utils/pushNotifications';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

// --- SOUS-COMPOSANT : 1 CHAMP MATHLIVE = 1 LIGNE PHYSIQUE DE LA PHOTO ---
const LigneMathLive = ({ index, valeurInitiale, onChange }) => {
  const mfRef = useRef(null);

  useEffect(() => {
    const mathField = mfRef.current;
    if (mathField) {
      // 🛡️ BOUCLIER ANTI-MOTS COLLÉS :
      // Si Gemini oublie de mettre \text{}, MathLive écrase les espaces.
      // On remplace donc préventivement les vrais espaces par des espaces LaTeX " \ "
      let safeValue = valeurInitiale || "";
      if (!safeValue.includes('\\text')) {
         safeValue = safeValue.replace(/ /g, '\\ ');
      }
      
      mathField.value = safeValue;
      
      const handleInput = (e) => onChange(index, e.target.value);
      mathField.addEventListener('input', handleInput);
      return () => mathField.removeEventListener('input', handleInput);
    }
  }, []); // Le tableau vide empêche l'éditeur de sauter pendant la frappe

  return (
    <div className="w-full bg-[#FAFAFC] border-2 border-slate-100 rounded-2xl p-2.5 focus-within:border-blue-400 focus-within:bg-white transition-all flex items-center gap-3">
      {/* Petit indicateur visuel de ligne */}
      <span className="text-slate-300 font-bold text-xs shrink-0 select-none">{index + 1}.</span>
      
      {/* FIX MOBILE : Wrapper avec overflow-x-auto et min-w-0 pour forcer le scrolling propre */}
      <div className="flex-1 min-w-0 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <math-field 
          ref={mfRef} 
          style={{ 
            fontSize: '1.15rem', 
            minWidth: '100%', 
            minHeight: '35px',
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#334155'
          }}
        />
      </div>
    </div>
  );
};

// --- COMPOSANT : ÉDITEUR MULTI-LIGNES ---
const EditeurMathVisuel = ({ qKey, lignesInitiales, onSave }) => {
  const [lignes, setLignes] = useState(lignesInitiales);

  const handleLigneChange = (index, nouvelleValeur) => {
    const nouvellesLignes = [...lignes];
    nouvellesLignes[index] = nouvelleValeur;
    setLignes(nouvellesLignes);
    onSave(qKey, nouvellesLignes);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="bg-white rounded-3xl p-5 mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 relative"
    >
      {/* 🪄 MAGIE CSS : On masque le clavier et le menu de MathLive pour une UI hyper clean */}
      <style>{`
        math-field::part(virtual-keyboard-toggle) { display: none !important; }
        math-field::part(menu-toggle) { display: none !important; }
      `}</style>

      <div className="flex items-center justify-between mb-4">
        <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg font-black text-sm tracking-widest uppercase">
          {qKey}
        </span>
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Touche une ligne pour corriger</span>
      </div>

      <div className="flex flex-col gap-2.5">
        {lignes.length > 0 ? (
          lignes.map((ligne, i) => (
            <LigneMathLive 
              key={i} 
              index={i} 
              valeurInitiale={ligne} 
              onChange={handleLigneChange} 
            />
          ))
        ) : (
          <span className="text-slate-400 italic text-sm text-center py-4">Rien n'a été lu.</span>
        )}
      </div>
    </motion.div>
  );
};

// --- COMPOSANTS UI REUSISABLES ---
const AbstractBlob = ({ seedText, isDone }) => {
  const colors = [['#3B82F6', '#22D3EE'], ['#8B5CF6', '#818CF8'], ['#F97316', '#FB7185'], ['#10B981', '#2DD4BF'], ['#EC4899', '#F43F5E']];
  const hash = seedText ? seedText.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
  const colorPair = isDone ? ['#CBD5E1', '#E2E8F0'] : colors[hash % colors.length];
  
  return (
    <div className={`relative w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 flex items-center justify-center ${isDone ? 'grayscale opacity-50' : ''}`}>
      <motion.div 
        animate={!isDone ? { rotate: [0, 10, -10, 0], borderRadius: ["40% 60% 70% 30%", "50% 50% 40% 60%", "60% 40% 30% 70%", "40% 60% 70% 30%"] } : {}}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-0 shadow-inner"
        style={{ background: `linear-gradient(135deg, ${colorPair[0]}, ${colorPair[1]})` }}
      />
      <BookOpen className="w-6 h-6 text-white relative z-10 opacity-80" />
    </div>
  );
};

// --- COMPOSANT PRINCIPAL ---
const MesDevoirs = ({ onLogout }) => {
  const [todoHomeworks, setTodoHomeworks] = useState([]);
  const [doneHomeworks, setDoneHomeworks] = useState([]);
  const [activeTab, setActiveTab] = useState('todo');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classInfo, setClassInfo] = useState({ name: 'Chargement...', teacherName: '...' });
  const navigate = useNavigate();

  const [currentView, setCurrentView] = useState('list'); 
  const [ocrData, setOcrData] = useState(null);
  const [currentImageFile, setCurrentImageFile] = useState(null);

  const [expandedCardId, setExpandedCardId] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [activeHomeworkId, setActiveHomeworkId] = useState(null);
  const fileInputRef = useRef(null);

  const [userInfo] = useState(() => JSON.parse(sessionStorage.getItem('user_info') || '{}'));
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);

  // --- ÉTATS CAMÉRA ---
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [capturedImage, setCapturedImage] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [showValidationPrompt, setShowValidationPrompt] = useState(false);
  const webcamRef = useRef(null);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const timer = setTimeout(() => setShowNotificationBanner(true), 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleEnableNotifications = async () => {
    const success = await subscribeUserToPush();
    if (success) setShowNotificationBanner(false);
    else alert("Impossible d'activer les alertes. Vérifiez les réglages de votre appareil.");
  };

  useEffect(() => {
    if (!userInfo || !Object.keys(userInfo).length) {
      setError("Session expirée. Veuillez vous reconnecter.");
      setLoading(false);
      return;
    }

    const fetchHomeworksAndClass = async () => {
      try {
        let currentClassroomId = userInfo.classroom_id;
        let currentClassName = 'Classe non assignée';
        let currentTeacherId = null;
        let currentTeacherName = 'Professeur';

        if (!currentClassroomId && userInfo.firstName && userInfo.lastName) {
          const fullName = `${userInfo.firstName} ${userInfo.lastName}`;
          const { data: classData } = await supabase.from('classrooms').select('id, name, teacher_id').contains('invited_students', JSON.stringify([fullName])).maybeSingle();
          if (classData) {
            currentClassroomId = classData.id;
            currentClassName = classData.name;
            currentTeacherId = classData.teacher_id;
            await supabase.from('users').update({ classroom_id: classData.id }).eq('id', userInfo.id);
            sessionStorage.setItem('user_info', JSON.stringify({ ...userInfo, classroom_id: classData.id }));
          }
        } else if (currentClassroomId) {
          const { data: classData } = await supabase.from('classrooms').select('name, teacher_id').eq('id', currentClassroomId).maybeSingle();
          if (classData) {
            currentClassName = classData.name;
            currentTeacherId = classData.teacher_id;
          }
        }

        if (currentTeacherId) {
          const { data: teacherData } = await supabase.from('users').select('first_name, last_name').eq('id', currentTeacherId).maybeSingle();
          if (teacherData) currentTeacherName = `${teacherData.first_name} ${teacherData.last_name}`;
        }

        if (!currentClassroomId) {
          setError("Accès restreint. Vous n'êtes rattaché à aucune classe active.");
          setLoading(false);
          return;
        }

        setClassInfo({ name: currentClassName, teacherName: currentTeacherName });

        const { data: hwData, error: hwError } = await supabase.from('homeworks').select('*').eq('classroom_id', currentClassroomId).order('created_at', { ascending: false });
        if (hwError) throw hwError;

        const { data: subData, error: subError } = await supabase.from('homework_submissions').select('*').eq('student_id', userInfo.id).eq('status', 'COMPLETED');
        if (subError) throw subError;

        const submissionsMap = {};
        if (subData) subData.forEach(sub => submissionsMap[sub.homework_id] = sub);

        const todo = [];
        const done = [];

        (hwData || []).forEach(hw => {
          if (submissionsMap[hw.id]) done.push({ ...hw, submission: submissionsMap[hw.id] });
          else todo.push(hw);
        });

        setTodoHomeworks(todo);
        setDoneHomeworks(done);

      } catch (err) {
        setError("Une erreur de synchronisation est survenue.");
      } finally {
        setLoading(false);
      }
    };

    fetchHomeworksAndClass();
  }, [userInfo]); 

  const handleLogout = () => {
    if (onLogout) onLogout(); 
    else {
      sessionStorage.removeItem('user_info');
      navigate('/auth');
    }
  };

  const formatDeadline = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const isPast = date < today;
    
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    let text = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    if (diffDays === 0) text = "Aujourd'hui";
    else if (diffDays === 1) text = "Demain";

    return { text, isPast };
  };

  const handleCardClick = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  const processImageForOCR = (file) => {
    setIsCameraOpen(false);
    setCapturedImage(null);
    setCurrentView('loading_ocr');
    setCurrentImageFile(file);

    const formData = new FormData();
    formData.append('file', file);

    const baseUrl = API_URLS.ANALYZE_PROGRESSIVE.replace('/analyze-progressive', '');
    const extractUrl = `${baseUrl}/extract-ocr`;

    fetch(extractUrl, { method: 'POST', body: formData })
    .then(res => {
      if (!res.ok) throw new Error("Erreur serveur lors de la lecture");
      return res.json();
    })
    .then(data => {
      const parsedData = {};
      Object.keys(data.extracted_data).forEach(qKey => {
        const qData = data.extracted_data[qKey];
        if (typeof qData === 'object' && !Array.isArray(qData)) {
          parsedData[qKey] = Object.values(qData).map(v => v.toString().trim());
        } else if (Array.isArray(qData)) {
          parsedData[qKey] = qData;
        } else {
          parsedData[qKey] = [qData.toString().trim()];
        }
      });
      setOcrData(parsedData); 
      setCurrentView('validating_ocr');
    })
    .catch(err => {
      console.error("Erreur OCR:", err);
      setCurrentView('list');
      alert("Oops ! L'image est floue ou illisible. Essaie de reprendre la photo bien à plat 📸");
    });
  };

  const submitValidatedOCR = () => {
    setCurrentView('loading_analysis');
    setAnalysisData(null);

    const backendData = {};
    Object.keys(ocrData).forEach(qKey => {
      const lignesArray = ocrData[qKey];
      backendData[qKey] = {};
      lignesArray.forEach((ligneTexte, index) => {
        backendData[qKey][`L${index + 1}`] = ligneTexte;
      });
    });

    const formData = new FormData();
    formData.append('file', currentImageFile, 'devoir.jpg'); 
    formData.append('user_id', userInfo.id);
    formData.append('exercise_id', activeHomeworkId);
    formData.append('exam_mode', 'false');
    formData.append('validated_solutions', JSON.stringify(backendData)); 

    fetch(API_URLS.ANALYZE_PROGRESSIVE, { method: 'POST', body: formData })
    .then(res => res.json())
    .then(data => {
      setAnalysisData({ data, studentName: userInfo.firstName });
      setCurrentView('results');
    })
    .catch(err => {
      console.error("Erreur d'analyse:", err);
      setCurrentView('list');
      alert("Un problème de connexion est survenu. Réessaie !");
    });
  };

  const handleOcrItemSave = useCallback((qKey, newLignesArray) => {
    setOcrData(prev => ({ ...prev, [qKey]: newLignesArray }));
  }, []);

  const openCamera = (e, hwId) => {
    e.stopPropagation();
    setActiveHomeworkId(hwId);
    setCapturedImage(null);
    setShowValidationPrompt(false);
    setIsCameraOpen(true);
  };

  // --- NOUVELLE GESTION CAMÉRA & UX PHOTO ---
  const capturePhoto = useCallback(() => {
    // 1. Effet de flash ludique
    setIsFlashing(true);
    setTimeout(() => setIsFlashing(false), 150);

    // 2. Capture de l'image
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setCapturedImage(imageSrc);
      
      // 3. Gestion du compteur intelligent (1 fois sur 3)
      let count = parseInt(localStorage.getItem('spyrale_photo_count') || '0', 10);
      count += 1;
      localStorage.setItem('spyrale_photo_count', count.toString());

      if (count % 3 === 0) {
        // Affiche la modale de validation
        setShowValidationPrompt(true);
      } else {
        // Envoi automatique fluide après 1 seconde d'animation
        setTimeout(() => {
          submitCapturedPhoto(imageSrc);
        }, 1200);
      }
    }
  }, [webcamRef]);

  const submitCapturedPhoto = async (base64Str) => {
    try {
      const res = await fetch(base64Str);
      const blob = await res.blob();
      processImageForOCR(blob);
    } catch (e) {
      console.error("Erreur transformation image", e);
    }
  };

  const toggleCamera = () => setFacingMode(prev => prev === "environment" ? "user" : "environment");
  const handleGalleryChoice = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !activeHomeworkId) return;
    processImageForOCR(file);
    e.target.value = null; 
  };

  const handleBackToList = () => window.location.reload(); 

  // --- RENDUS CONDITIONNELS DES VUES ---

  if (currentView === 'loading_ocr') {
    return <Loading mode="ocr" analysisData={null} onStartTransition={() => {}} />;
  }

  if (currentView === 'loading_analysis') {
    return <Loading mode="analysis" analysisData={analysisData} onStartTransition={() => setCurrentView('results')} />;
  }

  if (currentView === 'results') {
    return <SmartEx data={analysisData?.data} onBack={handleBackToList} isProgressive={true} studentName={analysisData?.studentName || userInfo.firstName} homeworkId={activeHomeworkId} />;
  }

  if (currentView === 'validating_ocr') {
    return (
      <div className="min-h-[100dvh] bg-[#F8FAFC] text-slate-900 font-sans pb-32 selection:bg-blue-200">
        <div className="sticky top-0 bg-[#F8FAFC]/90 backdrop-blur-xl z-20 px-5 py-5 border-b border-slate-200/50 shadow-sm">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-600 shadow-sm border border-slate-100">
                <MessageSquare size={24} className="fill-blue-50" />
              </div>
              <div>
                <h1 className="text-xl font-black tracking-tight leading-none mb-1">Dernière retouche</h1>
                <p className="text-xs font-medium text-slate-500">Corrige si l'IA s'est trompée</p>
              </div>
            </div>
            <button onClick={() => setCurrentView('list')} className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-600 active:scale-95">
              <X size={18} />
            </button>
          </motion.div>
        </div>

        <div className="px-5 mt-8 max-w-lg mx-auto">
          <div className="flex flex-col">
            <AnimatePresence>
              {ocrData && Object.keys(ocrData).map((qKey) => (
                <EditeurMathVisuel 
                  key={qKey} 
                  qKey={qKey} 
                  lignesInitiales={ocrData[qKey]} 
                  onSave={handleOcrItemSave} 
                />
              ))}
            </AnimatePresence>
          </div>
        </div>

        <motion.div 
          initial={{ y: 100 }} animate={{ y: 0 }} transition={{ type: "spring", damping: 25 }}
          className="fixed bottom-0 inset-x-0 p-5 bg-gradient-to-t from-white via-white to-transparent z-30"
        >
          <div className="max-w-lg mx-auto flex gap-3 pt-6">
            <button 
              onClick={() => setCurrentView('list')}
              className="w-16 flex-shrink-0 h-16 bg-slate-100 text-slate-600 rounded-[1.25rem] flex items-center justify-center hover:bg-slate-200 active:scale-95 transition-all shadow-sm border border-slate-200"
            >
              <Camera size={26} />
            </button>
            <button 
              onClick={submitValidatedOCR}
              className="flex-1 h-16 bg-black text-white rounded-[1.25rem] font-black text-lg flex items-center justify-center gap-2 active:scale-95 transition-all shadow-xl shadow-black/20"
            >
              Envoyer <ArrowRight size={22} />
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const currentList = activeTab === 'todo' ? todoHomeworks : doneHomeworks;

  return (
    <div className="min-h-[100dvh] bg-[#F8FAFC] text-slate-900 font-sans overflow-x-hidden selection:bg-blue-200">
      
      <AnimatePresence>
        {showNotificationBanner && (
          <motion.div initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -100, opacity: 0 }} className="fixed top-4 inset-x-4 z-[100] max-w-md mx-auto">
            <div className="bg-black text-white p-4 rounded-3xl shadow-2xl flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="flex gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-2xl flex items-center justify-center"><Bell size={20} className="text-blue-400" /></div>
                  <div>
                    <p className="font-bold text-sm">Active tes alertes ! 🎯</p>
                    <p className="text-xs text-slate-400 mt-0.5 leading-snug">Sois notifié quand M. {classInfo.teacherName.split(' ').pop()} donne un devoir.</p>
                  </div>
                </div>
                <button onClick={() => setShowNotificationBanner(false)} className="text-slate-500 hover:text-white"><X size={18} /></button>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={handleEnableNotifications} className="bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-xs font-bold active:scale-95 transition-all">Activer</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* =========================================================
          NOUVELLE INTERFACE APPAREIL PHOTO LUDIQUE ET FLUIDE
          ========================================================= */}
      <AnimatePresence>
        {isCameraOpen && (
          <motion.div initial={{ opacity: 0, y: "100%" }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 200 }} className="fixed inset-0 z-[999] bg-black flex flex-col">
            
            {/* Effet Flash Shutter */}
            <AnimatePresence>
              {isFlashing && <motion.div initial={{opacity: 1}} animate={{opacity: 0}} exit={{opacity: 0}} className="absolute inset-0 bg-white z-[1000] pointer-events-none" />}
            </AnimatePresence>

            <div className="absolute top-0 inset-x-0 p-6 flex justify-between items-center z-20 pt-safe-top">
              <button onClick={() => { setIsCameraOpen(false); setCapturedImage(null); setShowValidationPrompt(false); }} className="w-12 h-12 bg-black/50 backdrop-blur-xl rounded-full flex items-center justify-center text-white active:scale-90 transition-all"><X size={24} /></button>
              {!capturedImage && <button onClick={toggleCamera} className="w-12 h-12 bg-black/50 backdrop-blur-xl rounded-full flex items-center justify-center text-white active:scale-90 transition-all"><SwitchCamera size={24} /></button>}
            </div>
            
            <div className="relative flex-1 bg-black overflow-hidden flex items-center justify-center">
              {!capturedImage ? (
                <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" videoConstraints={{ facingMode: facingMode }} className="absolute w-full h-full object-cover" />
              ) : (
                // Animation de l'image figée pour l'aspect ludique
                <motion.img initial={{scale: 1.05}} animate={{scale: 1}} transition={{ type: "spring", stiffness: 300, damping: 20 }} src={capturedImage} className="absolute w-full h-full object-cover" />
              )}
              
              {/* Le cadre de guidage */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6">
                <div className="w-full max-w-sm aspect-[3/4] border-2 border-white/20 rounded-3xl relative shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]">
                  {!capturedImage && (
                    <div className="absolute -top-12 inset-x-0 text-center">
                      <span className="bg-black/50 backdrop-blur-md text-white text-xs font-bold px-4 py-2 rounded-full tracking-widest uppercase">Cadre ton exercice</span>
                    </div>
                  )}
                  <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-white rounded-tl-3xl transition-all"></div>
                  <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-white rounded-tr-3xl transition-all"></div>
                  <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-white rounded-bl-3xl transition-all"></div>
                  <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-white rounded-br-3xl transition-all"></div>
                </div>
              </div>
            </div>

            {/* Contrôles du bas : Capture ou Validation */}
            {!capturedImage ? (
              <div className="h-40 bg-black flex items-center justify-around pb-safe-bottom z-20 px-8">
                <button onClick={handleGalleryChoice} className="flex flex-col items-center gap-2 text-white/80 active:scale-95 transition-all">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><ImageIcon size={24} /></div>
                  <span className="text-[10px] font-bold uppercase tracking-wider">Galerie</span>
                </button>
                <button onClick={capturePhoto} className="w-20 h-20 rounded-full border-4 border-white p-1 active:scale-90 transition-transform">
                  <div className="w-full h-full bg-white rounded-full"></div>
                </button>
                <div className="w-12"></div>
              </div>
            ) : showValidationPrompt ? (
              // MODALE BOTTOM-SHEET 1 FOIS SUR 3
              <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ type: "spring", damping: 25 }} className="absolute bottom-0 inset-x-0 bg-white rounded-t-3xl p-6 pt-8 pb-safe-bottom z-30 shadow-2xl">
                <div className="w-12 h-1.5 bg-slate-200 rounded-full absolute top-3 left-1/2 -translate-x-1/2"></div>
                <h3 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">🧐 C'est bien net ?</h3>
                <p className="text-slate-500 text-[14px] leading-relaxed mb-6">Assure-toi que les calculs soient bien lisibles pour que le professeur puisse corriger parfaitement.</p>
                <div className="flex gap-3">
                  <button onClick={() => { setCapturedImage(null); setShowValidationPrompt(false); }} className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-[1.25rem] font-bold active:scale-95 transition-all">
                    Recommencer
                  </button>
                  <button onClick={() => submitCapturedPhoto(capturedImage)} className="flex-1 py-4 bg-black text-white rounded-[1.25rem] font-bold active:scale-95 transition-all flex items-center justify-center gap-2 shadow-xl shadow-black/20">
                    <CheckCircle2 size={20} /> C'est parfait !
                  </button>
                </div>
              </motion.div>
            ) : (
              // LOADER PENDANT L'ENVOI AUTOMATIQUE (les autres fois)
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="absolute bottom-12 inset-x-8 bg-black/40 backdrop-blur-xl border border-white/20 rounded-[1.5rem] p-5 flex items-center justify-center gap-3 z-30 text-white shadow-2xl">
                <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                <span className="font-bold tracking-wide">Lecture de la copie...</span>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />

      <header className="pt-safe-top bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="px-5 py-4 flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-tr from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-black text-lg shadow-md">
              {userInfo.firstName?.charAt(0) || 'S'}
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-black tracking-widest uppercase text-slate-400">{classInfo.name}</span>
              <span className="text-sm font-bold text-slate-800">Salut, {userInfo.firstName || 'Toi'} 👋</span>
            </div>
          </div>
          <button onClick={handleLogout} className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center active:bg-slate-200 transition-colors">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pt-8 pb-32">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 mb-6">Missions.</h1>

        {!loading && !error && (
          <div className="flex p-1 bg-slate-200/50 rounded-2xl mb-8">
            <button 
              onClick={() => { setActiveTab('todo'); setExpandedCardId(null); }}
              className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all relative ${activeTab === 'todo' ? 'text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              {activeTab === 'todo' && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white rounded-xl shadow-sm" />}
              <span className="relative z-10 flex items-center justify-center gap-2">
                À faire <span className={`px-2 py-0.5 rounded-md text-[10px] ${activeTab === 'todo' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'}`}>{todoHomeworks.length}</span>
              </span>
            </button>
            <button 
              onClick={() => { setActiveTab('done'); setExpandedCardId(null); }}
              className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all relative ${activeTab === 'done' ? 'text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              {activeTab === 'done' && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white rounded-xl shadow-sm" />}
              <span className="relative z-10 flex items-center justify-center gap-2">
                Terminés <span className={`px-2 py-0.5 rounded-md text-[10px] ${activeTab === 'done' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>{doneHomeworks.length}</span>
              </span>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="flex gap-2">
              <motion.div animate={{ y: [-10, 0, -10] }} transition={{ duration: 1, repeat: Infinity }} className="w-3 h-3 bg-blue-500 rounded-full" />
              <motion.div animate={{ y: [-10, 0, -10] }} transition={{ duration: 1, delay: 0.2, repeat: Infinity }} className="w-3 h-3 bg-purple-500 rounded-full" />
              <motion.div animate={{ y: [-10, 0, -10] }} transition={{ duration: 1, delay: 0.4, repeat: Infinity }} className="w-3 h-3 bg-pink-500 rounded-full" />
            </div>
          </div>
        ) : error ? (
          <div className="p-5 rounded-3xl bg-red-50 border border-red-100 text-red-800 font-medium text-sm flex items-start gap-3">
            <AlertTriangle className="shrink-0 text-red-500" />
            <p>{error}</p>
          </div>
        ) : currentList.length === 0 ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] border border-slate-100 p-10 text-center shadow-sm">
            <div className="w-20 h-20 mx-auto bg-slate-50 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 size={40} className="text-slate-300" />
            </div>
            <h3 className="text-xl font-black text-slate-800 mb-1">{activeTab === 'todo' ? 'Zéro devoir !' : 'Aucun historique'}</h3>
            <p className="text-sm font-medium text-slate-500">{activeTab === 'todo' ? "Tu es à jour, profite de ton temps libre 🎮" : "Tes exercices terminés apparaîtront ici."}</p>
          </motion.div>
        ) : (
          <motion.div className="flex flex-col gap-4">
            <AnimatePresence>
              {currentList.map((hw) => {
                const deadlineInfo = formatDeadline(hw.questions?.deadline);
                const isExpanded = expandedCardId === hw.id;
                const isDone = activeTab === 'done';
                
                const score = hw.submission?.evaluations?.score ?? 0;
                let scoreColor = "bg-slate-100 text-slate-600";
                if (isDone) {
                  if (score >= 80) scoreColor = "bg-emerald-100 text-emerald-700";
                  else if (score >= 50) scoreColor = "bg-amber-100 text-amber-700";
                  else scoreColor = "bg-red-100 text-red-700";
                }

                return (
                  <motion.div 
                    layout
                    key={hw.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => !isDone && handleCardClick(hw.id)}
                    whileTap={!isDone ? { scale: 0.98 } : {}}
                    className={`bg-white rounded-3xl p-5 border transition-shadow ${
                      isDone ? 'border-slate-100 opacity-90' : 
                      isExpanded ? 'border-blue-200 shadow-[0_10px_40px_rgba(59,130,246,0.1)]' : 'border-slate-100 shadow-sm active:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <AbstractBlob seedText={hw.chapter} isDone={isDone} />
                      <div className="flex-1 min-w-0 pt-1">
                        <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 block mb-1 truncate">
                          {hw.chapter}
                        </span>
                        <h3 className="text-lg font-black leading-tight text-slate-900 mb-2 truncate">
                          {hw.exercise_info || "Exercice"}
                        </h3>
                        
                        {!isDone && (
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold ${deadlineInfo?.isPast ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                            <Clock size={12} /> {deadlineInfo?.text || 'Sans date'}
                          </div>
                        )}
                      </div>

                      {isDone && (
                        <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-2xl ${scoreColor} shrink-0`}>
                          <span className="text-[10px] font-bold uppercase opacity-70">Note</span>
                          <span className="text-base font-black">{score}%</span>
                        </div>
                      )}
                      
                      {!isDone && !isExpanded && (
                        <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 self-center">
                          <ChevronRight size={20} />
                        </div>
                      )}
                    </div>

                    <AnimatePresence>
                      {isExpanded && !isDone && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-5 mt-4 border-t border-slate-100">
                            <button 
                              onClick={(e) => openCamera(e, hw.id)} 
                              className="w-full flex items-center justify-center gap-3 py-4 bg-black text-white font-black text-base rounded-2xl active:scale-95 transition-transform shadow-lg shadow-black/10"
                            >
                              <Camera size={20} /> Flasher mon devoir
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default MesDevoirs;